import { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';
import { PASSING_GRADE, getGradeBand } from '../../../shared/constant/grading';
import { TERMS } from '../../../shared/constant/terms';
import { getStudentGrades } from '../services/advisoryService';

const gradeClass = (grade) => (grade != null && grade < PASSING_GRADE ? 'text-red-600 font-medium' : 'text-gray-700');

const navButton =
  'flex items-center gap-1 rounded-md border border-[#4ea6d0] px-3 py-1.5 text-sm font-medium text-[#0b4a71] hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent';

// One student's term grades in every subject, with Previous/Next to move through the class.
function StudentRecordModal({ students, index, subjects, onIndexChange, onClose }) {
  const student = students[index];
  const cache = useRef({});
  const [grades, setGrades] = useState(cache.current[student.id] ?? null);
  const [error, setError] = useState('');

  const hasPrev = index > 0;
  const hasNext = index < students.length - 1;

  useEffect(() => {
    setError('');
    if (cache.current[student.id]) {
      setGrades(cache.current[student.id]);
      return undefined;
    }

    let mounted = true;
    setGrades(null);
    getStudentGrades(student.id)
      .then((result) => {
        cache.current[student.id] = result;
        if (mounted) setGrades(result);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load this record');
      });

    return () => {
      mounted = false;
    };
  }, [student.id]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [index, hasPrev, hasNext, onIndexChange]);

  const gradeOf = (subjectId, term) => grades?.[`${subjectId}|${term}`];

  // The general average of a term counts only the subjects graded so far.
  const averageOf = (term) => {
    const termGrades = subjects.map((s) => gradeOf(s.id, term)).filter((g) => g != null);
    return termGrades.length ? termGrades.reduce((sum, g) => sum + g, 0) / termGrades.length : null;
  };

  const fullName = `${student.lastname}, ${student.firstname} ${student.middlename ?? ''}`.trim();

  return (
    <Modal title={fullName} onClose={onClose} maxWidthClass="max-w-2xl">
      <div className="space-y-3">
        {error ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        ) : grades === null ? (
          <p className="text-center text-gray-500 py-10">Loading…</p>
        ) : subjects.length === 0 ? (
          <p className="text-center text-gray-500 py-10">No subjects are assigned to this section yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-[#4ea6d0] text-left text-white">
                <tr>
                  <th className="px-4 py-2 font-medium">Subject</th>
                  {TERMS.map((t) => (
                    <th key={t} className="px-4 py-2 font-medium">Term {t}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subjects.map((subject) => (
                  <tr key={subject.id}>
                    <td className="px-4 py-2 font-medium text-[#0b4a71]">{subject.name}</td>
                    {TERMS.map((t) => {
                      const grade = gradeOf(subject.id, t);
                      return (
                        <td key={t} className={`px-4 py-2 ${gradeClass(grade)}`}>{grade ?? '—'}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                <tr>
                  <td className="px-4 py-2 font-medium text-[#0b4a71]">General Average</td>
                  {TERMS.map((t) => {
                    const average = averageOf(t);
                    return (
                      <td key={t} className={`px-4 py-2 font-medium ${gradeClass(average)}`}>
                        {average == null ? '—' : average.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-[#0b4a71]">Remarks</td>
                  {TERMS.map((t) => {
                    const band = getGradeBand(averageOf(t));
                    return (
                      <td key={t} className="px-4 py-2">
                        {band ? (
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${band.tone}`}>
                            {band.remark}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={() => onIndexChange(index - 1)} disabled={!hasPrev} className={navButton}>
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              Student {index + 1} of {students.length}
            </span>
            <button type="button" onClick={() => onIndexChange(index + 1)} disabled={!hasNext} className={navButton}>
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default StudentRecordModal;
